<template>
  <v-app>
    <v-container fluid>
      <template v-if="$insertAccess($route.path)">
        <v-form ref="form" @submit.prevent="submitFormHandler">
          <v-row>
            <v-col>
              <v-expansion-panels v-model="claimPanel">
                <v-expansion-panel>
                  <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                    <span>{{
                      isEntryMode ? "Add New Claim" : "Update Claim"
                    }}</span>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <!-- Master Level Date Field -->
                    <v-row class="pl-2 pr-2 pt-2 pb-0" align="center">
                      <v-col lg="3" cols="12">
                        <v-date-input input-format="dd/mm/yyyy" v-model="masterDate" label="Claim Date" variant="outlined" density="compact"
                          bg-color="grey-lighten-4" prepend-icon="" prepend-inner-icon="mdi-calendar"
                          autocomplete="off" clearable>
                          <template #label>
                            Claim Date <span class="text-red-accent-4">*</span>
                          </template>
                        </v-date-input>
                      </v-col>
                    </v-row>

              
                    <!-- Claim Details Rows -->
                    <template v-for="(detail, index) in claimDetails" :key="index">
                      <!-- First Row: Claim Type, Amount, From Date, To Date, Buttons -->
                      <v-row class="pl-2 pr-2 pb-2 pt-4" style="border-bottom: 1px solid #ccc;" align-center no-gutters dense>
                        <v-col cols="12" sm="2" md="3" class="pr-4 pa-0">
                          <v-autocomplete v-model="detail.type" label="Claim Type" variant="outlined" density="compact"
                            bg-color="grey-lighten-4" class="ma-0" :items="claimTypeList" item-title="name"
                            item-value="id" autocomplete="off" clearable>
                            <template #label>
                              Claim Type <span class="text-red-accent-4">*</span>
                            </template>
                          </v-autocomplete>
                        </v-col>

                        <v-col cols="12" lg="2" md="3" class="pr-4 pa-0">
                          <v-text-field v-model.number="detail.amount" label="Amount" variant="outlined"
                            density="compact" bg-color="grey-lighten-4" class="ma-0" type="number" step="0.01"
                            clearable>
                            <template #label>
                              Amount <span class="text-red-accent-4">*</span>
                            </template>
                          </v-text-field>
                        </v-col>

                        <v-col cols="12" lg="3" md="2" class="pr-4 pa-0">
                          <v-date-input input-format="dd/mm/yyyy" v-model="detail.claim_date_from" label="From Date" variant="outlined"
                            density="compact" bg-color="grey-lighten-4" class="ma-0" prepend-icon=""
                            prepend-inner-icon="mdi-calendar-start" autocomplete="off" clearable>
                            <template #label>
                              From Date <span class="text-red-accent-4">*</span>
                            </template>
                          </v-date-input>
                        </v-col>

                        <v-col cols="12" lg="3" md="2" class="pr-4 pa-0">
                          <v-date-input input-format="dd/mm/yyyy" v-model="detail.claim_date_to" label="To Date" variant="outlined"
                            density="compact" bg-color="grey-lighten-4" class="ma-0" prepend-icon=""
                            prepend-inner-icon="mdi-calendar-end" autocomplete="off" clearable>
                            <template #label>
                              To Date <span class="text-red-accent-4">*</span>
                            </template>
                          </v-date-input>
                        </v-col>

                        <v-col sm="4" md="1" class="d-flex justify-center gap-2">
                          <v-btn @click="removeClaimDetailRow(index)" variant="tonal" color="primary"
                            v-if="index !== claimDetails.length - 1">
                            <v-icon color="red" icon="mdi-delete" size="large"></v-icon>
                          </v-btn>

                          <v-btn @click="addClaimDetailRow" variant="tonal" color="primary" v-else>
                            <v-icon color="info" icon="mdi-plus" size="large"></v-icon>
                          </v-btn>
                        </v-col>

                         <v-col lg="6" md="6" cols="12">
                          <v-text-field v-model="detail.description" label="Description" variant="outlined"
                            density="compact" bg-color="grey-lighten-4" class="ma-0" rows="2" clearable></v-text-field>
                        </v-col>

                        <v-col cols="12" md="5" lg="5" class="align-center">

                          <v-file-input
                            v-model="detail.files"
                            label="Attachment"
                            variant="outlined"
                            density="compact"
                            hide-details
                            :show-size="false"
                            class="file-align"
                            chips
                            multiple
                            :clearable="false"
                          >
                            <template #selection="{ fileNames }">
                              <v-chip
                                v-for="(fileName, fileIndex) in fileNames"
                                :key="fileIndex"
                                size="small"
                                closable
                                @click:close="deleteChip(index, fileIndex)"
                              >
                                {{ fileName }}
                              </v-chip>
                            </template>
                          </v-file-input>
                        </v-col>
                      </v-row>

                  
                    </template>
                    <!-- Master Level Remarks -->
                    <v-row class="pl-2 pr-2">
                      <v-col md="6" cols="12">
                        <v-textarea v-model="remarks" label="Remarks" variant="outlined" density="compact"
                          bg-color="grey-lighten-4" rows="2" clearable></v-textarea>
                      </v-col>
                    </v-row>

                    <!-- Action Buttons -->
                    <v-card-actions>
                      <v-row class="pt-2">
                        <v-col cols="12">
                          <v-layout class="justify-end">
                            <v-btn class="bg-grey-darken-1 me-2" variant="flat" @click="submitFormAsDraft"
                              :loading="draftLoading" :disabled="draftLoading || postLoading">
                              DRAFT
                            </v-btn>
                            <v-btn class="bg-primary" variant="flat" type="submit" :loading="postLoading"
                              :disabled="draftLoading || postLoading">
                              {{ buttonText }}
                            </v-btn>
                          </v-layout>
                        </v-col>
                      </v-row>
                    </v-card-actions>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </v-container>
  </v-app>
</template>


<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  components: {},
  data: () => ({
    itemsPerPage: 10,
    claimPanel: [0],
    search: "",
    claimTypeLoading: false,
    buttonText: 'POST',
  }),
  created() {
    const p = this.$store.dispatch("hrmClaimTypeSetup/getClaimTypeList");
    if (p && typeof p.catch === "function") p.catch(() => {});
  },
  setup() {
    const masterDate = ref('');
    const claimDetails = ref([
      {
        type: '',
        amount: '',
        claim_date_from: '',
        claim_date_to: '',
        description: '',
        files: [],
      },
    ]);

    const addClaimDetailRow = () => {
      claimDetails.value.push({
        type: '',
        amount: '',
        claim_date_from: '',
        claim_date_to: '',
        description: '',
        files: [],
      });
    };

    const removeClaimDetailRow = (index) => {
      if (claimDetails.value.length > 1) {
        claimDetails.value.splice(index, 1);
      }
    };

    const deleteChip = (detailIndex, fileIndex) => {
      const detail = claimDetails.value[detailIndex];
      if (detail?.files && Array.isArray(detail.files)) {
        detail.files.splice(fileIndex, 1);
      }
    };

    const remarks = ref('');
    const errorMessages = ref("");
    const draftLoading = ref(false);
    const postLoading = ref(false);
    const isEntryMode = ref(true);
    const currentStatus = ref(0);
    const editingClaimId = ref(null);
    const editingClaimRef = ref(null); // Original claim data for computing removed attachments

    const store = useStore();
    const router = useRouter();

    const claimTypeList = computed(() => {
      const list = store.getters["hrmClaimTypeSetup/getHrmClaimTypeList"];
      return Array.isArray(list) ? list.filter((item) => item.status == 1) : [];
    });

    const validateForm = () => {
      errorMessages.value = "";

      if (!masterDate.value) {
        errorMessages.value = "Claim Date is required";
        return false;
      }

      if (!claimDetails.value || claimDetails.value.length === 0) {
        errorMessages.value = "At least one claim detail is required";
        return false;
      }

      // Validate all details
      for (let detail of claimDetails.value) {
        if (!detail.type) {
          errorMessages.value = "Claim Type is required for all details";
          return false;
        }
        if (!detail.amount || detail.amount <= 0) {
          errorMessages.value = "Valid Amount is required for all details";
          return false;
        }
        if (!detail.claim_date_from) {
          errorMessages.value = "From Date is required for all details";
          return false;
        }
        if (!detail.claim_date_to) {
          errorMessages.value = "To Date is required for all details";
          return false;
        }
        if (new Date(detail.claim_date_from) > new Date(detail.claim_date_to)) {
          errorMessages.value = "From Date must be before To Date";
          return false;
        }
      }

      return true;
    };

    const submitClaim = (status) => {
      if (!validateForm()) {
        return;
      }

      const formData = new FormData();

      // Append master level fields
      formData.append('date', formattedDate(masterDate.value) || '');
      formData.append('remarks', remarks.value || '');
      formData.append('status', status);

      // If editing, include claim_master_id
      if (!isEntryMode.value && editingClaimId.value) {
        formData.append('claim_master_id', editingClaimId.value);
      }

      // Append details array fields individually
      claimDetails.value.forEach((detail, index) => {
        formData.append(`details[${index}][type]`, detail.type || '');
        formData.append(
          `details[${index}][claim_date_from]`,
          formattedDate(detail.claim_date_from) || ''
        );
        formData.append(
          `details[${index}][claim_date_to]`,
          formattedDate(detail.claim_date_to) || ''
        );
        formData.append(`details[${index}][description]`, detail.description || '');
        formData.append(`details[${index}][amount]`, detail.amount || 0);
        if (detail.id) {
          formData.append(`details[${index}][id]`, detail.id);
        }
        // Handle files separately for the details array
        // Only send actual File objects (new files), not existing file objects
        if (detail.files && detail.files.length > 0) {
          detail.files.forEach((file, fileIndex) => {
            // Only append actual File objects (new files), skip existing files (with isExisting flag)
            if (file instanceof File && !file.isExisting) {
              formData.append(`details[${index}][files][${fileIndex}]`, file);
            }
          });
        }
      });

      // Set loading state based on status
      if (status === 0) {
        draftLoading.value = true;
      } else {
        postLoading.value = true;
      }

      // Collect attachment IDs user removed (using existing delete API - no backend changes)
      const idsToDelete = [];
      if (editingClaimRef.value?.details) {
        claimDetails.value.forEach((detail) => {
          if (!detail.id) return;
          const originalDetail = editingClaimRef.value.details.find((d) => d.id === detail.id);
          const originalIds = (originalDetail?.attachments || []).map((a) => a.id);
          const remainingIds = (detail.files || [])
            .filter((f) => f.isExisting && f.attachment_id)
            .map((f) => f.attachment_id);
          originalIds.filter((id) => !remainingIds.includes(id)).forEach((id) => idsToDelete.push(id));
        });
      }

      const deleteRemovedAttachments = () =>
        Promise.all(idsToDelete.map((id) => store.dispatch("deleteClaimAttachment", id)));

      deleteRemovedAttachments()
        .then(() => store.dispatch("saveUpdateClaim", formData))
        .then((response) => {
          draftLoading.value = false;
          postLoading.value = false;
          errorMessages.value = "";
          toast.success(response.data?.message || "Claim saved successfully", {
            timeout: 3000,
          });
          store.dispatch("getClaimList");
          setTimeout(() => {
            router.push({ path: "/hrm/claim-list" });
          }, 2000);
        })
        .catch((error) => {
          draftLoading.value = false;
          postLoading.value = false;
          const errMsg = error.response?.data?.message || error.message || 'Error saving claim';
          errorMessages.value = errMsg;
          console.error("Error saving claim:", error);
          toast.error(errMsg);
        });
    };

    return {
      submitClaim,
      remarks,
      masterDate,
      claimDetails,
      addClaimDetailRow,
      removeClaimDetailRow,
      deleteChip,
      errorMessages,
      isEntryMode,
      draftLoading,
      postLoading,
      claimTypeList,
      currentStatus,
      editingClaimId,
      editingClaimRef,
    };
  },
  methods: {
    submitFormHandler() {
      this.submitClaim(1);
    },
    submitFormAsDraft() {
      this.submitClaim(0);
    },
  },
  mounted() {
    const editingClaim = this.$store.getters.getEditingHrmClaim;
    if (editingClaim) {
      const setRef = (key, value) => {
        const r = this[key];
        if (r != null && typeof r === 'object' && 'value' in r) {
          r.value = value;
        } else {
          this[key] = value;
        }
      };

      setRef('isEntryMode', false);
      setRef('editingClaimId', editingClaim.id);
      setRef('editingClaimRef', editingClaim);
      this.buttonText = 'Update';

      setRef('masterDate', editingClaim.date ?? '');
      setRef('remarks', editingClaim.remarks ?? '');

      const mappedDetails = editingClaim.details && editingClaim.details.length > 0
        ? editingClaim.details.map((d) => ({
            id: d.id,
            type: d.type_id,
            amount: d.amount,
            claim_date_from: d.claim_date_from ?? '',
            claim_date_to: d.claim_date_to ?? '',
            description: d.description ?? '',
            files: d.attachments ? d.attachments.map(file => ({
              name: file.file.split('/').pop(),  // Use 'name' instead of 'file_name'
              file_url: file.file,  // Save the full file path for later use
              isExisting: true, // Flag to identify existing files
              attachment_id: file.id, // Store attachment ID
            })) : [],
          }))
        : [];
       
      setRef('claimDetails', mappedDetails);
    }
  },
  beforeUnmount() {
    this.$store.commit('UNSET_EDITING_HRM_CLAIM');
  },
};
</script>

<style scoped>

.file-align .v-field {
  min-height: 40px !important;
}

.file-align .v-input__control {
  min-height: 40px !important;
}

</style>
